import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { merge, pick } from 'lodash-es';
import { useDisclosure } from '@chakra-ui/react';
import { PLATFORM } from '../../../../constants/benchat';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../constants/recaptchable';
import authenticationService from '../../../../service/authenticationService';
import pnotifyService from '../../../../service/pnotifyService';
import { guardApiWithCaptcha } from '../../../../customElements/sl_armor_captcha/utils/armorCaptchaGuard';
import { ARMOR_CAPTCHA_SCENES } from '../../../../customElements/sl_armor_captcha';
import TabContent from '../../components/TabContent';
import { SECTION_STATUS, TAB_TYPE } from '../../constants';
import { useAppContext } from '../../context/appContext';
import { getTabUrl } from '../../utils/tab';
import EmailVerificationModal from './components/EmailVerificationModal';
import ErrorPanel from './components/ErrorPanel';
import Loading from './components/Loading';
import { FIELD } from './constants/field';
import { MESSAGE_TYPE, ERROR_CODE } from './constants/message';
import { MessageContextProvider } from './context/messageContext';
import { ProfileContextProvider } from './context/profileContext';
import {
  checkMobileIsConfirmed,
  fallbackMobilePhone,
  getActiveMarketingSubscriptions,
  transformFormDataToPayload,
} from './utils';
import Profile from './Profile';

const ProfileIndex = () => {
  const { t } = useTranslation();
  const {
    currentUser,
    isRecaptchaEnabled,
    recaptchaSiteKey,
    slFeatureService,
    userSetting,
    grecaptcha,
  } = useAppContext();
  const userId = currentUser._id;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const reCAPTCHARef = useRef();
  const newEmail = useRef('');
  const [messages, setMessages] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [status, setStatus] = useState(SECTION_STATUS.LOADING);
  const [userData, setUserData] = useState(null);
  const [cachedData, setCachedData] = useState(null);
  const hasShoplineEmailCaptcha =
    slFeatureService.hasFeature('shopline_captcha') &&
    slFeatureService.hasFeature('shopline_email_captcha');
  const enabledRecaptcha = isRecaptchaEnabled || hasShoplineEmailCaptcha;
  const getUserInfo = useCallback(
    async ({ isBackgroundSync = false } = {}) => {
      try {
        if (!isBackgroundSync) {
          setStatus(SECTION_STATUS.LOADING);
        }
        const response = await authenticationService.getUser(userId);
        setUserData(response.data.data);
        if (!isBackgroundSync) {
          setStatus(SECTION_STATUS.SUCCESS);
        }
      } catch (error) {
        console.error(error);
        if (!isBackgroundSync) {
          setStatus(SECTION_STATUS.FAILURE);
        }
      }
    },
    [userId],
  );

  const handleSubmit = async (formData, shoplineCaptchaToken = {}) => {
    try {
      setCachedData(null);
      setSubmitError(null);
      setStatus(SECTION_STATUS.LOADING);
      const subscriptions = await getActiveMarketingSubscriptions(userId);
      const payload = {
        ...shoplineCaptchaToken,
        user: transformFormDataToPayload({
          hasBroadcastToUnsubscribeSms: slFeatureService.hasFeature(
            'broadcast_to_unsubscribers_sms',
          ),
          hasCountryCode: slFeatureService.hasFeature('country_code'),
          smsSubscriptions: subscriptions[PLATFORM.SMS] || [],
          formData,
          userData,
          userSetting,
        }),
        benchatFields: {
          subscriptions: {
            line: JSON.stringify(subscriptions[PLATFORM.LINE_MES_API] || []),
            facebook: JSON.stringify(subscriptions[PLATFORM.FACEBOOK] || []),
          },
        },
        includes: ['membership_tier_gap'],
      };
      if (enabledRecaptcha) {
        payload[RECAPTCHA_TOKEN_FIELD] =
          await reCAPTCHARef.current?.executeAsync();
        reCAPTCHARef.current?.reset();
      }
      const response = await guardApiWithCaptcha({
        scene: ARMOR_CAPTCHA_SCENES.PROFILE_UPDATE,
        request: (extra) =>
          authenticationService.updateUser(userId, { ...payload, ...extra }),
      });
      if (!response.data.result) {
        setCachedData(formData);
        setStatus(SECTION_STATUS.SUCCESS);
        throw response;
      }
      if (response.data.data.info_reward_triggered) {
        pnotifyService.notify(t('users.membership.member_info.sent'), {});
      }
      if (!checkMobileIsConfirmed(userData, formData, userSetting)) {
        setCachedData(fallbackMobilePhone({ userSetting, userData, formData }));
        setStatus(SECTION_STATUS.SUCCESS);
        throw new Error('users.error.not_save_mobile_phone', {
          cause: {
            [FIELD.MOBILE_PHONE]: 'users.error.not_save_mobile_phone',
          },
        });
      }
      let successMessage = 'users.save.success';
      if (
        userSetting.email_verification.status &&
        formData.email &&
        formData.email !== userData.email
      ) {
        if (userSetting.login_with_verification.status) {
          successMessage = 'users.save.email_verification.success';
        }
        if (response.data.data.trial_email_quota_exceeded) {
          pnotifyService.notify(
            t('verification.email.trial_merchant.reach_limit.failure'),
            {
              customClass: 'error',
              icon: 'fa fa-exclamation-triangle',
            },
          );
        } else if (userSetting.login_with_verification.status) {
          newEmail.current = formData.email;
          onOpen();
        } else {
          pnotifyService.notify(t('users.save.email_verification.send'), {});
        }
      }
      pnotifyService.notify(t(successMessage));
      setUserData(response.data.data);
      setStatus(SECTION_STATUS.SUCCESS);
    } catch (e) {
      const error = e?.data?.data || e?.cause || e?.response?.data;
      switch (true) {
        // from e.data.data
        case error?.email?.[0] === 'is already taken':
          setSubmitError({
            [FIELD.EMAIL]: 'form.validation.unique',
          });
          break;
        // for e?.response.data.error_code
        case error?.error_code === ERROR_CODE.RECAPTCHA_VERIFICATION_FAILED:
          setCachedData(merge(formData, pick(userData, [FIELD.EMAIL])));
          setStatus(SECTION_STATUS.SUCCESS);
          setMessages([
            {
              type: MESSAGE_TYPE.ERROR,
              message: error.error_messages?.[0],
            },
          ]);
          break;
        // from e.data.data
        case Boolean(error?.base?.[0]):
          setSubmitError({
            [FIELD.MOBILE_PHONE]: 'session.mobile_center.check_phone.error',
          });
          break;
        // from e.data.data
        case Boolean(error?.mobile_phone_with_country_calling_code?.[0]):
          setSubmitError({
            [FIELD.MOBILE_PHONE]: 'session.forget_passowrd.check_phone.error',
          });
          break;
        // from e.cause
        case Boolean(error?.[FIELD.MOBILE_PHONE]):
          setMessages([
            {
              type: MESSAGE_TYPE.ERROR,
              message: t(error[FIELD.MOBILE_PHONE]),
            },
          ]);
          break;
        // from e?.response?.data?.error;
        // for email change limit reached
        case Array.isArray(error?.error):
          // restore email from userData
          setCachedData(merge(formData, pick(userData, [FIELD.EMAIL])));
          setStatus(SECTION_STATUS.SUCCESS);
          setMessages([
            {
              type: MESSAGE_TYPE.ERROR,
              message: error.error[0],
            },
          ]);
          break;
        default:
          setStatus(SECTION_STATUS.FAILURE);
          console.error(error);
      }
    }
  };

  useEffect(() => {
    getUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TabContent url={getTabUrl(TAB_TYPE.PROFILE, userId)}>
      <MessageContextProvider messages={messages} setMessages={setMessages}>
        <ProfileContextProvider
          userData={userData}
          getUserInfo={getUserInfo}
          submitError={submitError}
        >
          {status === SECTION_STATUS.LOADING && <Loading />}
          {status === SECTION_STATUS.FAILURE && (
            <ErrorPanel onReload={getUserInfo} />
          )}
          {status === SECTION_STATUS.SUCCESS && (
            <Profile cachedData={cachedData} onSubmit={handleSubmit} />
          )}
          {enabledRecaptcha && (
            <ReCAPTCHA
              ref={reCAPTCHARef}
              size="invisible"
              grecaptcha={grecaptcha}
              sitekey={recaptchaSiteKey}
            />
          )}
          <EmailVerificationModal
            isOpen={isOpen}
            onClose={onClose}
            email={newEmail.current}
          />
        </ProfileContextProvider>
      </MessageContextProvider>
    </TabContent>
  );
};

export default ProfileIndex;
